import { useUserStoryUploadFlow } from '@src/stores-v2/StoreContext';
import { useRef } from 'react';

import useProcessingProgress, {
  type UploadPhaseStory,
} from './useProcessingProgress';

export type { UploadPhaseStory };

/**
 * Read the upload phase for a story and return a unified 0–100 percent that
 * spans both the upload (0–50) and server-side processing (50–100) phases.
 *
 * Must be called inside an `observer` component so MobX can track the
 * observable access and trigger re-renders on phase changes.
 *
 * NOTE: Deliberately avoids `useMemo` — the previous implementation's
 * dependency on `store.storyUploadPhaseRecord[storyId]` did not reliably
 * trigger re-computation when MobX updated the record.
 */
export const useUploadPhase = (story: UploadPhaseStory) => {
  const store = useUserStoryUploadFlow();
  const phase = store.getStoryUploadPhase(story.id);
  const processingPercent = useProcessingProgress(story, phase);

  // Per-instance monotonic floor: once the bar advances, don't let a
  // transient phase gap (idle / error reset) drag it back within this row.
  const lastPercentRef = useRef(0);
  const prevPhaseRef = useRef<string | undefined>(undefined);

  let percent: number | undefined;
  if (phase.phase === 'queued') percent = 0;
  else if (phase.phase === 'uploading') percent = phase.progress * 0.5;
  else if (phase.phase === 'processing') percent = processingPercent ?? 50;
  // The upload-flow store marks 'complete' the moment the kickoff API call
  // returns, but the backend continues encoding/transcribing for minutes
  // afterwards. Keep showing the processing animation until the story
  // actually has its assets (processingPercent will be 100 when truly ready).
  else if (phase.phase === 'complete') percent = processingPercent ?? 100;

  // Detect transitions that should ratchet the floor back to 0:
  //  - new queued upload (existing behavior)
  //  - retry restarting at uploading.progress === 0 from a non-queued phase
  //  - returning to idle (e.g. modal cleared the phase entry)
  const isFreshStart =
    phase.phase === 'idle' ||
    phase.phase === 'queued' ||
    (phase.phase === 'uploading' &&
      phase.progress === 0 &&
      prevPhaseRef.current !== 'queued' &&
      prevPhaseRef.current !== 'uploading');

  if (isFreshStart) {
    lastPercentRef.current = 0;
  }
  prevPhaseRef.current = phase.phase;

  if (phase.phase === 'error') {
    return { phase, percent: undefined };
  }

  const lastPercent = lastPercentRef.current;
  if (typeof percent === 'number') {
    const next = Math.max(lastPercent, percent);
    lastPercentRef.current = next;
    percent = next;
  } else if (lastPercent > 0) {
    percent = lastPercent;
  }

  return { phase, percent };
};

export default useUploadPhase;
